import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  BellRing,
  Info,
  Calendar,
  Users,
  Target,
  Clock,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import { supabase } from "@/integrations/supabase/client";

interface Campaign {
  id?: number;
  title: string; // Changed from 'name' to 'title'
  message: string; // Changed from 'description' to 'message'
  type: string;
  status: string;
  template_id?: number; // Optional now
  group_id: number; // Changed from 'user_group_id' to 'group_id'
  scheduled_at?: string; // Changed from 'scheduled_for' to 'scheduled_at'
  target_count?: number;
  sent_count?: number;
  delivered_count?: number;
  opened_count?: number;
  clicked_count?: number;
  ab_test_enabled?: boolean;
  control_group_percentage?: number; // Changed from 'ab_test_config'
  priority?: string;
  expires_at?: string;
  created_by?: number;
}

interface NotificationTemplate {
  id: number;
  name: string;
  category: string;
  title_template: string;
  body_template: string;
}

interface UserGroup {
  id: number;
  name: string;
  description: string;
  member_count: number;
}

const CAMPAIGN_TYPES = [
  { value: "notification", label: "Notification" },
  { value: "promotion", label: "Promotion" },
  { value: "offer", label: "Special Offer" },
  { value: "announcement", label: "Announcement" },
];

const CAMPAIGN_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "scheduled", label: "Scheduled" },
  { value: "sending", label: "Sending" },
  { value: "sent", label: "Sent" },
  { value: "cancelled", label: "Cancelled" },
];

const PRIORITY_LEVELS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

export default function NotificationCampaignForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  const [formData, setFormData] = useState<Campaign>({
    title: "",
    message: "",
    type: "notification",
    status: "draft",
    template_id: undefined,
    group_id: 0,
    scheduled_at: "",
    ab_test_enabled: false,
    control_group_percentage: 0,
    priority: "normal",
  });

  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<NotificationTemplate | null>(null);
  const [selectedUserGroup, setSelectedUserGroup] = useState<UserGroup | null>(
    null
  );
  const [previewEstimate, setPreviewEstimate] = useState<number | null>(null);

  const isEditing = !!id;

  useEffect(() => {
    fetchTemplates();
    fetchUserGroups();
    if (isEditing) {
      fetchCampaign();
    }
  }, [id]);

  const fetchTemplates = async () => {
    try {
      const { data, error } = await supabase
        .from("notification_templates")
        .select("id, name, category, title_template, body_template")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setTemplates(data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
    }
  };

  const fetchUserGroups = async () => {
    try {
      const { data, error } = await supabase
        .from("user_groups")
        .select("id, name, description, member_count")
        .eq("is_active", true)
        .order("name");

      if (error) throw error;
      setUserGroups(data || []);
    } catch (error) {
      console.error("Error fetching user groups:", error);
    }
  };

  const fetchCampaign = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("group_campaigns")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      if (data) {
        console.log("Fetched campaign data:", data);
        setFormData({
          ...data,
          scheduled_at: data.scheduled_at
            ? new Date(data.scheduled_at).toISOString().slice(0, 16)
            : "",
          expires_at: data.expires_at
            ? new Date(data.expires_at).toISOString().slice(0, 16)
            : "",
        });

        // Set selected template and user group
        if (data.template_id) {
          const template = templates.find((t) => t.id === data.template_id);
          setSelectedTemplate(template || null);
        }

        if (data.group_id) {
          const group = userGroups.find((g) => g.id === data.group_id);
          setSelectedUserGroup(group || null);
          setPreviewEstimate(group?.member_count || null);
        }
      }
    } catch (error) {
      console.error("Error fetching campaign:", error);
      toast({
        title: "Error",
        description: "Failed to fetch campaign details.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (templateId: string) => {
    const template = templates.find((t) => t.id === parseInt(templateId));
    setSelectedTemplate(template || null);
    setFormData((prev) => ({ ...prev, template_id: parseInt(templateId) }));
  };

  const handleUserGroupChange = (groupId: string) => {
    const group = userGroups.find((g) => g.id === parseInt(groupId));
    setSelectedUserGroup(group || null);
    setFormData((prev) => ({ ...prev, group_id: parseInt(groupId) }));
    setPreviewEstimate(group?.member_count || null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: "Error",
        description: "Campaign title is required.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.template_id) {
      toast({
        title: "Error",
        description: "Please select a notification template.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.group_id) {
      toast({
        title: "Error",
        description: "Please select a user group.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      const payload = {
        title: formData.title,
        message: formData.message,
        type: formData.type,
        status: formData.status,
        template_id: formData.template_id || null,
        group_id: formData.group_id,
        scheduled_at: formData.scheduled_at
          ? new Date(formData.scheduled_at).toISOString()
          : null,
        expires_at: formData.expires_at
          ? new Date(formData.expires_at).toISOString()
          : null,
        target_count: previewEstimate || 0,
        ab_test_enabled: formData.ab_test_enabled,
        control_group_percentage: formData.control_group_percentage,
        priority: formData.priority,
      };

      console.log("Submitting payload:", payload);

      if (isEditing) {
        const { error } = await supabase
          .from("group_campaigns")
          .update(payload)
          .eq("id", id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("group_campaigns")
          .insert(payload);

        if (error) throw error;
      }

      toast({
        title: "Success",
        description: `Campaign ${
          isEditing ? "updated" : "created"
        } successfully.`,
      });

      navigate("/admin/notifications/campaigns");
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast({
        title: "Error",
        description: `Failed to ${
          isEditing ? "update" : "create"
        } campaign: ${error}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5); // Minimum 5 minutes from now
    return now.toISOString().slice(0, 16);
  };

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  const backButton = (
    <Button
      variant="ghost"
      onClick={() => navigate("/admin/notifications/campaigns")}
    >
      <ArrowLeft className="h-4 w-4 mr-2" />
      Back to Campaigns
    </Button>
  );

  return (
    <AdminLayout
      title={isEditing ? "Edit Campaign" : "Create Campaign"}
      actionButton={backButton}
      mainClassName="flex-1 overflow-y-auto p-6 bg-background max-w-4xl mx-auto"
    >
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Campaign Details</CardTitle>
            <CardDescription>
              Configure the basic information for your notification campaign
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Campaign Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="e.g., Monthly Book Recommendations, New Feature Announcement"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Campaign Message</Label>
              <Textarea
                id="message"
                value={formData.message}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, message: e.target.value }))
                }
                placeholder="Describe the purpose and goals of this campaign..."
                rows={3}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Campaign Type *</Label>
                <Select
                  value={formData.type}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, type: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Status *</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, status: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {CAMPAIGN_STATUSES.map((status) => (
                      <SelectItem key={status.value} value={status.value}>
                        {status.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, priority: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITY_LEVELS.map((priority) => (
                      <SelectItem key={priority.value} value={priority.value}>
                        {priority.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Template & Audience */}
        <Card>
          <CardHeader>
            <CardTitle>Template & Audience</CardTitle>
            <CardDescription>
              Choose the notification template and target audience for your
              campaign
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="template_id">
                Notification Template *
              </Label>
              <Select
                value={
                  formData.template_id ? String(formData.template_id) : ""
                }
                onValueChange={handleTemplateChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a template" />
                </SelectTrigger>
                <SelectContent>
                  {templates.map((template) => (
                    <SelectItem key={template.id} value={String(template.id)}>
                      <div className="flex items-center gap-2">
                        <span>{template.name}</span>
                        <Badge variant="outline" className="text-xs">
                          {template.category}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedTemplate && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <h4 className="font-semibold mb-2">Template Preview:</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <strong>Title:</strong> {selectedTemplate.title_template}
                  </div>
                  <div>
                    <strong>Body:</strong> {selectedTemplate.body_template}
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="group_id">Target Audience *</Label>
              <Select
                value={formData.group_id ? String(formData.group_id) : ""}
                onValueChange={handleUserGroupChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user group" />
                </SelectTrigger>
                <SelectContent>
                  {userGroups.map((group) => (
                    <SelectItem key={group.id} value={String(group.id)}>
                      <div className="flex items-center justify-between w-full">
                        <span>{group.name}</span>
                        <Badge variant="outline" className="text-xs ml-2">
                          {group.member_count || 0} users
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedUserGroup && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-4 w-4" />
                  <h4 className="font-semibold">
                    Target Group: {selectedUserGroup.name}
                  </h4>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  {selectedUserGroup.description}
                </p>
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-blue-600" />
                  <span className="text-sm">
                    <strong>Estimated reach:</strong>{" "}
                    {selectedUserGroup.member_count || 0} users
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scheduling */}
        <Card>
          <CardHeader>
            <CardTitle>Scheduling</CardTitle>
            <CardDescription>
              Configure when your campaign should be sent
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertDescription>
                Leave scheduled time empty to send immediately when the campaign
                status is set to "sending".
              </AlertDescription>
            </Alert>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="scheduled_at">Scheduled For</Label>
                <Input
                  id="scheduled_at"
                  type="datetime-local"
                  value={formData.scheduled_at}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      scheduled_at: e.target.value,
                    }))
                  }
                  min={getMinDateTime()}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="expires_at">Expires At (Optional)</Label>
                <Input
                  id="expires_at"
                  type="datetime-local"
                  value={formData.expires_at}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      expires_at: e.target.value,
                    }))
                  }
                  min={getMinDateTime()}
                />
              </div>
            </div>

            {formData.scheduled_at && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-green-600" />
                  <span className="text-sm">
                    <strong>Campaign will be sent:</strong>{" "}
                    {new Date(formData.scheduled_at).toLocaleString()}
                  </span>
                </div>
              </div>
            )}

            {formData.expires_at && (
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <span className="text-sm">
                    <strong>Campaign expires:</strong>{" "}
                    {new Date(formData.expires_at).toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* A/B Testing */}
        <Card>
          <CardHeader>
            <CardTitle>A/B Testing</CardTitle>
            <CardDescription>
              Enable A/B testing to optimize your campaign performance
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <Label htmlFor="ab_test_enabled">Enable A/B Testing</Label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Split your audience to test different versions of your
                  campaign
                </p>
              </div>
              <Switch
                id="ab_test_enabled"
                checked={formData.ab_test_enabled}
                onCheckedChange={(checked) =>
                  setFormData((prev) => ({ ...prev, ab_test_enabled: checked }))
                }
              />
            </div>

            {formData.ab_test_enabled && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="control_group_percentage">
                    Control Group Percentage
                  </Label>
                  <Input
                    id="control_group_percentage"
                    type="number"
                    min="0"
                    max="50"
                    value={formData.control_group_percentage}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        control_group_percentage: parseInt(e.target.value) || 0,
                      }))
                    }
                    placeholder="10"
                  />
                  <p className="text-xs text-muted-foreground">
                    Percentage of users who will receive the control version
                    (0-50%)
                  </p>
                </div>

                <Alert>
                  <Zap className="h-4 w-4" />
                  <AlertDescription>
                    A/B testing will split your audience between the control
                    group and test variants. The control group percentage
                    determines how many users receive the original version.
                  </AlertDescription>
                </Alert>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/notifications/campaigns")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading
              ? "Saving..."
              : isEditing
              ? "Update Campaign"
              : "Create Campaign"}
          </Button>
        </div>
      </form>
    </AdminLayout>
  );
}
